import { useState, useEffect, useCallback, type ReactElement } from 'react';
import { X } from 'lucide-react';
import { useCreateArrangement } from '@/lib/queries/pieces';
import { useKeys, useForms } from '@/lib/queries/reference-data';
import { Button } from '@/components/atoms/button';
import { FormInput } from '@/components/molecules/form-input';
import { Micro } from '@/components/atoms/micro';
import type { Arrangement } from '@/lib/types';

type Props = {
  pieceId: string;
  pieceName: string;
  onClose: () => void;
  onCreated: (arrangement: Arrangement) => void;
};

function toCanonicalName(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
}

export function CreateArrangementDialog({ pieceId, pieceName, onClose, onCreated }: Props): ReactElement {
  const [name, setName] = useState(pieceName);
  const [keyId, setKeyId] = useState('');
  const [formId, setFormId] = useState('');
  const [error, setError] = useState<string | null>(null);

  const createArrangement = useCreateArrangement();
  const { data: keysData } = useKeys();
  const { data: formsData } = useForms();

  const handleEscape = useCallback(
    (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    },
    [onClose],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  const handleSubmit = async (): Promise<void> => {
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const result = await createArrangement.mutateAsync({
        pieceId,
        prettyName: trimmed,
        canonicalName: toCanonicalName(trimmed),
        keyId: keyId || undefined,
        formId: formId || undefined,
      });
      const selectedKey = keysData?.content.find((k) => k.keyId === result.keyId);
      const selectedForm = formsData?.content.find((f) => f.formId === result.formId);
      onCreated({
        arrangementId: result.arrangementId,
        pieceId,
        pieceName,
        prettyName: result.prettyName,
        canonicalName: result.canonicalName,
        arrangerId: null,
        arrangerName: null,
        keyId: result.keyId,
        keyName: selectedKey?.prettyName ?? null,
        timeSignatureId: null,
        timeSignatureName: null,
        formId: result.formId,
        formName: selectedForm?.prettyName ?? null,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create arrangement');
    }
  };

  const selectClass =
    'h-12 w-full rounded-xl border border-[var(--rule)] bg-[var(--bg-surface)] px-4 text-sm font-medium text-[var(--ink)] focus:border-[var(--brand)] focus:outline-none focus:ring-2 focus:ring-[color:var(--brand)]/20';

  return (
    <div className="fixed inset-0 z-[60] flex items-end justify-center sm:items-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Create new arrangement"
        className="relative w-full max-w-lg space-y-5 rounded-t-2xl border border-[var(--rule)] bg-[var(--bg-surface)] p-6 shadow-xl sm:rounded-2xl"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close dialog"
          className="absolute right-4 top-4 text-[var(--ink-3)] hover:text-[var(--ink)]"
        >
          <X className="h-5 w-5" />
        </button>

        <div>
          <h2 className="serif text-2xl font-black tracking-tight text-[var(--ink)]">
            New arrangement
          </h2>
          <p className="mt-1 text-sm text-[var(--ink-3)]">{pieceName}</p>
        </div>

        <FormInput
          id="new-arrangement-name"
          label="Arrangement name"
          placeholder="e.g. Piano Solo"
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void handleSubmit();
          }}
          autoFocus
        />

        <div className="space-y-2">
          <Micro as="label" htmlFor="new-arrangement-key">Key (optional)</Micro>
          <select id="new-arrangement-key" value={keyId} onChange={(e) => setKeyId(e.target.value)} className={selectClass}>
            <option value="">— No key —</option>
            {keysData?.content.map((key) => (
              <option key={key.keyId} value={key.keyId}>
                {key.prettyName}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <Micro as="label" htmlFor="new-arrangement-form">Form (optional)</Micro>
          <select id="new-arrangement-form" value={formId} onChange={(e) => setFormId(e.target.value)} className={selectClass}>
            <option value="">— No form —</option>
            {formsData?.content.map((form) => (
              <option key={form.formId} value={form.formId}>
                {form.prettyName}
              </option>
            ))}
          </select>
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-xl border border-[color:var(--err)]/40 bg-[color:var(--err)]/10 px-3 py-2 text-sm text-[var(--err)]"
          >
            {error}
          </div>
        )}

        <div className="flex justify-end gap-3 border-t border-[var(--rule)] pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={!name.trim() || createArrangement.isPending}
          >
            {createArrangement.isPending ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </div>
    </div>
  );
}
